import { Injectable, OnDestroy } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Subject, Subscription, interval, timer } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { UserInfoService } from '../../../../../services/user-info.service';

export interface WebSocketEnvelope {
	action: string;
	message?: any;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
	private socket$: WebSocketSubject<WebSocketEnvelope> | null = null;
	private messagesSubject$ = new Subject<WebSocketEnvelope>();
	public messages$ = this.messagesSubject$.asObservable();
	private connectionState$ = new BehaviorSubject<ConnectionState>('disconnected');
	public connectionStateChanges$ = this.connectionState$.asObservable();
	private reconnectSub?: Subscription;
	private heartbeatSub?: Subscription;
	private manuallyClosed = false;

	// Reconnect config
	private readonly reconnectDelayMs = 3000;
	private readonly maxReconnectAttempts = 20;
	private reconnectAttempts = 0;

	constructor(private userInfoService: UserInfoService) {}

	public connect(): void {
		// Need to log the connection id when the connection is first created

		// already connected or in progress
		if (this.socket$ && !this.socket$.closed) {
			return;
		}

		this.manuallyClosed = false;
		this.connectionState$.next('connecting');

		const url = this.buildSocketUrl();

		this.socket$ = webSocket<WebSocketEnvelope>({
			url,
			serializer: (msg) => JSON.stringify(msg),
			deserializer: (e) => JSON.parse(e.data),
			openObserver: {
				next: () => {
					console.log('[WS] connected:', url);
					this.reconnectAttempts = 0;
					this.connectionState$.next('connected');
					this.startHeartbeat();
				},
			},
			closeObserver: {
				next: (event: CloseEvent) => {
					console.log('[WS] closed:', event.code, event.reason);
					this.connectionState$.next('disconnected');
					this.stopHeartbeat();

					// Manually call the connection closed of AWS Websocket Gateway to fire the disconnect method
					if (this.socket$ && !this.socket$.closed) {
						this.socket$.complete(); // triggers closeObserver
					}
					this.socket$ = null;

					if (!this.manuallyClosed) {
						this.scheduleReconnect();
					}
				},
			},
		});

		this.socket$.subscribe({
			next: (msg: WebSocketEnvelope) => {
				this.messagesSubject$.next(msg);
			},
			error: (err) => {
				console.error('[WS] error:', err);
				this.connectionState$.next('disconnected');
				this.socket$ = null;

				if (!this.manuallyClosed) {
					this.scheduleReconnect();
				}
			},
			complete: () => {
				console.log('[WS] completed');
			},
		});
	}

	public async connectUser(): Promise<void> {
		const loggedInUser = await this.userInfoService.getCurrentLoggedInUser();
		if (!loggedInUser) {
			return;
		}

		const data: WebSocketEnvelope = {
			action: 'connectUser',
			message: {
				internalUserGUID: loggedInUser.internalUserGUID,
			},
		};

		await this.send(data);
	}

	private buildSocketUrl(): string {
		const socketUrl = `${environment.webSocketApiUri}`;
		return socketUrl;
	}

	public async send(message: WebSocketEnvelope): Promise<void> {
		if (!this.socket$ || this.socket$.closed) {
			console.warn('[WS] not connected, attempting reconnect before send');

			this.connect();
			await this.connectUser();

			timer(300).subscribe(() => {
				if (this.socket$ && !this.socket$.closed) {
					this.socket$.next(message);
				} else {
					console.warn('[WS] still not connected, dropping message:', message);
				}
			});
			return;
		}

		this.socket$.next(message);
	}

	private scheduleReconnect(): void {
		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.warn('[WS] max reconnect attempts reached, giving up');
			return;
		}

		if (this.reconnectSub && !this.reconnectSub.closed) {
			return; // already scheduled
		}

		this.reconnectAttempts++;
		console.log(`[WS] scheduling reconnect attempt #${this.reconnectAttempts}`);

		this.reconnectSub = timer(this.reconnectDelayMs).subscribe(async () => {
			// Re-creates connection and also register the client, because every new recreate generates a new connectionId
			this.connect();
			await this.connectUser();
		});
	}

	private startHeartbeat(): void {
		this.stopHeartbeat();
		// Send a ping every 30s
		this.heartbeatSub = interval(30000).subscribe(() => {
			if (this.socket$ && !this.socket$.closed) {
				const message: WebSocketEnvelope = { action: 'ping' };
				this.socket$.next(message);
			}
		});
	}

	private stopHeartbeat(): void {
		if (this.heartbeatSub) {
			this.heartbeatSub.unsubscribe();
			this.heartbeatSub = undefined;
		}
	}

	public disconnect(): void {
		this.manuallyClosed = true;
		this.stopHeartbeat();
		if (this.reconnectSub) {
			this.reconnectSub.unsubscribe();
			this.reconnectSub = undefined;
		}
		if (this.socket$ && !this.socket$.closed) {
			this.socket$.complete(); // triggers closeObserver
		}
		this.socket$ = null;
		this.connectionState$.next('disconnected');
	}
	ngOnDestroy(): void {
		this.disconnect();
		if (this.reconnectSub) {
			this.reconnectSub.unsubscribe();
		}
	}
}
